import { useCallback, useEffect, useRef, useState } from 'react';

export interface ResizableWidth {
  width: number;
  min: number;
  max: number;
  dragging: boolean;
  /** Spread onto the drag handle element. */
  handleProps: {
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
    onDoubleClick: () => void;
  };
  reset: () => void;
}

interface Options {
  storageKey: string;
  defaultWidth: number;
  min: number;
  max: number;
  /** Space the rest of the layout must keep, used to clamp against narrow viewports. */
  reserve?: number;
  /** 'left' means the handle sits on the panel's left edge, so dragging left widens it. */
  edge?: 'left' | 'right';
}

const STEP = 24;

/**
 * Width of a panel the user can drag. The value is clamped to the viewport, remembered
 * across sessions, and adjustable from the keyboard so the handle is not mouse-only.
 */
export function useResizableWidth({
  storageKey,
  defaultWidth,
  min,
  max,
  reserve = 560,
  edge = 'left',
}: Options): ResizableWidth {
  const ceiling = useCallback(() => {
    if (typeof window === 'undefined') return max;
    return Math.max(min, Math.min(max, window.innerWidth - reserve));
  }, [max, min, reserve]);

  const clamp = useCallback(
    (value: number) => Math.round(Math.min(ceiling(), Math.max(min, value))),
    [ceiling, min],
  );

  const [width, setWidth] = useState<number>(() => {
    try {
      const saved = Number(localStorage.getItem(storageKey));
      if (Number.isFinite(saved) && saved > 0) return saved;
    } catch {
      /* storage unavailable */
    }
    return defaultWidth;
  });
  const [dragging, setDragging] = useState(false);
  const start = useRef({ x: 0, width: 0 });

  const commit = useCallback(
    (value: number) => {
      const next = clamp(value);
      setWidth(next);
      try {
        localStorage.setItem(storageKey, String(next));
      } catch {
        /* width lasts for this session only */
      }
    },
    [clamp, storageKey],
  );

  // Keep the panel sensible when the window changes size.
  useEffect(() => {
    function onResize() {
      setWidth((current) => clamp(current));
    }
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [clamp]);

  // While dragging, suppress selection and keep the resize cursor everywhere.
  useEffect(() => {
    if (!dragging) return;
    const previous = document.body.style.cursor;
    document.body.style.cursor = 'col-resize';
    document.body.classList.add('select-none');
    return () => {
      document.body.style.cursor = previous;
      document.body.classList.remove('select-none');
    };
  }, [dragging]);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      start.current = { x: event.clientX, width };
      setDragging(true);

      const target = event.currentTarget;
      const move = (move_: PointerEvent) => {
        const delta =
          edge === 'left' ? start.current.x - move_.clientX : move_.clientX - start.current.x;
        setWidth(clamp(start.current.width + delta));
      };
      const up = () => {
        setDragging(false);
        target.removeEventListener('pointermove', move);
        target.removeEventListener('pointerup', up);
        target.removeEventListener('pointercancel', up);
        setWidth((current) => {
          try {
            localStorage.setItem(storageKey, String(current));
          } catch {
            /* session only */
          }
          return current;
        });
      };
      target.addEventListener('pointermove', move);
      target.addEventListener('pointerup', up);
      target.addEventListener('pointercancel', up);
    },
    [clamp, edge, storageKey, width],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      const grow = edge === 'left' ? 'ArrowLeft' : 'ArrowRight';
      const shrink = edge === 'left' ? 'ArrowRight' : 'ArrowLeft';
      if (event.key === grow) commit(width + STEP);
      else if (event.key === shrink) commit(width - STEP);
      else if (event.key === 'Home') commit(min);
      else if (event.key === 'End') commit(ceiling());
      else return;
      event.preventDefault();
    },
    [ceiling, commit, edge, min, width],
  );

  const reset = useCallback(() => commit(defaultWidth), [commit, defaultWidth]);

  return {
    width,
    min,
    max: ceiling(),
    dragging,
    handleProps: { onPointerDown, onKeyDown, onDoubleClick: reset },
    reset,
  };
}
